import React, { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { apiPost } from '../api/client';
import type { LoginRequest, LoginResponse } from '../types/api';
import AlertMessage from '../components/AlertMessage';
import LoadingSpinner from '../components/LoadingSpinner';
import ReportProblemLink from '../components/ReportProblemLink';
import './LoginPage.css';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const body: LoginRequest = { email, password };
      const response = await apiPost<LoginResponse>('/api/auth/login', body);

      if (response.requires2FA) {
        navigate('/2fa-login', { state: { email } });
        return;
      }

      login(response);
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Sign in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <h1 className="auth-title">Cedar Vault</h1>
        <p className="auth-subtitle">Sign in to your vault</p>
        <div className="divider" />

        <AlertMessage type="error" message={error} />

        <form className="login-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label" htmlFor="email">Email</label>
            <input
              id="email"
              className="form-input"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoComplete="email"
              autoFocus
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="password">Password</label>
            <input
              id="password"
              className="form-input"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              autoComplete="current-password"
            />
          </div>

          <button className="btn-primary" type="submit" disabled={loading}>
            {loading ? <LoadingSpinner small /> : 'Sign In'}
          </button>
        </form>

        <div className="auth-links">
          <Link to="/forgot-password">Forgot password?</Link>
        </div>

        <Link to="/register" className="btn-secondary trial-cta">
          Start Your Free 14-Day Trial
          <span className="trial-cta-sub">No credit card required.</span>
        </Link>
        <p className="auth-site-link">
          <Link to="/help">Need help?</Link>
          &ensp;&middot;&ensp;
          <a href="https://cedarvaultapp.com" target="_blank" rel="noopener noreferrer">
            About Cedar Vault
          </a>
          &ensp;&middot;&ensp;
          <a href="https://cedarvaultapp.com/privacy" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>
          &ensp;&middot;&ensp;
          <ReportProblemLink />
        </p>
      </div>
    </div>
  );
}
